// Visual effects: every hero hits differently, skills telegraph before they land, statuses read at a
// glance. Ported from prototypes/match/js/fx.js. Two things changed because core's events are now plain
// data instead of live refs: (1) a `hero` id on an event is resolved to the real Hero before it reaches
// a handler, so handler bodies read exactly like before; (2) `dir` on an event is a plain {x,z} — the two
// handlers that ran Vector3 methods on it (addScaledVector) now build a THREE.Vector3 from it first.
import * as THREE from 'three';
import { mk, flat, TEAM, setMaskLook, setDying, release } from './engrave.js';
import { SKILLS } from '../core/skills.js';
import { totemDef } from '../core/artifacts.js';
import { Game } from '../core/game.js';
import { LocalGameView, type GameView, type ViewHero, type ViewLob, type ViewProjectile } from './gameView.js';
import type { Sound } from './sound.js';
import { MONSTER_LOOK } from './monsterView.js';
import { viewerConfused } from './delusion.js';
import { heroMaskLook } from './heroLook.js';
import { createHeadings } from './projectileHeading.js';
import { buildFalcon } from './birdFigures.js';
import { createBolts } from './fxBolts.js';
import { createImpacts } from './fxImpacts.js';
import { createSkillFx, heartGeometry } from './fxSkills.js';
import { createMomentFx } from './fxMoments.js';
import { wireHitBy } from './net/wireSanity.js';

const INK = 0x2d2119;
// Астрал: Тень's dusk, Грех's crimson, Кошмар's bruise-violet and his crows' near-black.
const DUSK = 0x3b3448, CRIMSON = 0xa8304a, BRUISE = 0x6a5a9a, CROW = 0x2b2430;
// Тотемы: Стойкости warm wood, a gold sun and a soft green-gold ring; Гнева dark red wood, bone horns, a blood ring.
const TOTEM_LOOK = {
  ward: { wood: 0x8a6a3e, mark: 0xd8a848, ring: 0xc8d884, mote: 0xe8e0a0 },
  wrath: { wood: 0x5a1e18, mark: 0xd8cfb8, ring: 0x8a1e1a, mote: 0x6a1a14 },
} as const;
// Связка: the cord's colour, and how long the thrown coil is in the air — visual only, core ties on release.
const CORD = 0xb07a5a, BIND_FLIGHT = 0.2;

const isVec = (v: unknown): v is { x: number; z: number } =>
  typeof v === 'object' && v !== null && Number.isFinite((v as any).x) && Number.isFinite((v as any).z);

/* Сокол на перчатке: a purely visual climb-and-dive arc over the falcon's flight progress `k` (0..1,
   core's `Projectile.arc` — see game.ts's updateProjectiles). Same shape offline, online and in replays,
   since all three ultimately read the same `arc`/`k` (fillProjectile mirrors it offline; the wire carries
   it as-is otherwise). `FALCON_PEAK` is where the climb tops out and the dive begins; both height and
   pitch use the same two-branch sin/cos envelope so they stay in sync (pitch is its slope). */
const FALCON_PEAK = 0.38, FALCON_RISE = 3.5, FALCON_Y0 = 1.8, FALCON_Y1 = 1.2, FALCON_PITCH_MAX = 1.0;
function falconHeight(k: number): number {
  const t = k <= FALCON_PEAK ? k / FALCON_PEAK : (k - FALCON_PEAK) / (1 - FALCON_PEAK);
  const climb = k <= FALCON_PEAK;
  const shape = climb ? Math.sin(t * Math.PI / 2) : Math.cos(t * Math.PI / 2);
  return FALCON_Y0 + (FALCON_Y1 - FALCON_Y0) * k + FALCON_RISE * shape;
}
function falconPitch(k: number): number {
  if (k <= FALCON_PEAK) return FALCON_PITCH_MAX * Math.cos((k / FALCON_PEAK) * Math.PI / 2);  // nose up, easing toward level at the peak
  return -FALCON_PITCH_MAX * Math.sin(((k - FALCON_PEAK) / (1 - FALCON_PEAK)) * Math.PI / 2);  // nose down, steepest right at impact
}

/* A crow: a four-sided body along +z and two wings hinged on it (flap = each wing's rotation.z). Hatched
   like a unit, not stippled like an fx, so a flock reads as drawn birds rather than particles. */
function crowMesh() {
  const g = new THREE.Group();
  const body = mk(new THREE.ConeGeometry(0.07, 0.34, 4), CROW, { team: 'unit', cast: false });
  body.rotation.x = Math.PI / 2;
  g.add(body);
  for (const side of [-1, 1]) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0.08, side * 0.44, 0.03, -0.05, 0, 0, -0.12], 3));
    geo.computeVertexNormals();
    const wing = mk(geo, CROW, { team: 'unit', cast: false, double: true });
    wing.userData.side = side;
    g.add(wing);
  }
  return g;
}
function flap(bird: THREE.Object3D, phase: number) {
  for (let i = 1; i < bird.children.length; i++) {
    const w = bird.children[i]!;
    w.rotation.z = w.userData.side * Math.sin(phase) * 0.9;
  }
}
/** Every mesh this file scales from a computed progress fraction (a one-shot effect's `k = (t-t0)/dur`, a
    zone's grow-then-fade curve) must be refused loudly rather than drawn if that fraction is not a plain
    finite number in `[0, 1]` — this is the guard the giant-red-disc report asked for placed at the DRAW
    site, not the wire boundary (net/wireSanity.ts's own guard covers the wire-VALUE half of the same class
    of bug; this covers the LOCAL-COMPUTATION half, which a wire-side check can never see: a stale `t0`
    ticked against the wrong clock produces a huge-but-locally-computed number that was never on the wire at
    all). One line of defense, not the fix for whatever produces a bad `k` in the first place — see this
    file's own `spawn`/live-tick loop for where that is applied. */
const warnedFrac = new Set<string>();
function saneProgress(k: number, label: string): boolean {
  if (Number.isFinite(k)) return true;
  if (!warnedFrac.has(label)) {
    warnedFrac.add(label);
    console.error(`[fx] '${label}' computed a non-finite progress fraction (${k}) — expiring it instead of drawing it`);
  }
  return false;
}
/** Clamps a grow/fade curve's OWN output, not its input `k` (zones() computes `k` outside [0,1] on
    purpose, past a zone's own life, to derive a fade-out — see its own comment) — what must never reach
    a `.scale.setScalar()` call is a non-finite or wildly out-of-range RESULT, regardless of how it got
    that way (a division by a zero/negative `life`, in particular — see this task's own report). */
function saneGrow(grow: number, label: string): number {
  if (Number.isFinite(grow) && grow >= -0.01 && grow <= 1.5) return Math.max(0, Math.min(1, grow));
  if (!warnedFrac.has(label)) {
    warnedFrac.add(label);
    console.error(`[fx] '${label}' computed an insane grow/fade value (${grow}) — drawing it as fully faded instead`);
  }
  return 0;
}

/* Melee marks per character: ring radii, arc length, colour and flourishes. */
const SLASH: Record<string, any> = {
  rhino: { inner: 0.9, outer: 1.45, arc: 2.1, color: INK, y: 0.6, dur: 0.22, dust: true },
  durer: { inner: 1.55, outer: 1.75, arc: 2.5, color: 0x5a5650, y: 1.0, dur: 0.17 },
  landsknecht: { inner: 0.5, outer: 1.5, arc: 1.3, color: INK, y: 0.5, dur: 0.26, splat: true, dust: true },
  death: { inner: 1.35, outer: 1.6, arc: 2.9, color: 0x281c30, y: 0.9, dur: 0.26 },
  ferryman: { inner: 1.8, outer: 2.0, arc: 2.6, color: 0x4a4a52, y: 1.0, dur: 0.28 },
  devil: { inner: 1.1, outer: 1.4, arc: 2.2, color: 0x6a1a18, y: 0.8, dur: 0.22, double: true, embers: true },
};

/* Jagged ink blot: a disc with a noisy rim, a few variants reused. */
function blotGeometry(seed: number) {
  const pts: THREE.Vector2[] = [], n = 22;
  let s = seed;
  const r = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2, rad = 0.55 + r() * 0.45 + (i % 5 === 0 ? r() * 0.5 : 0);
    pts.push(new THREE.Vector2(Math.cos(a) * rad, Math.sin(a) * rad));
  }
  return new THREE.ShapeGeometry(new THREE.Shape(pts));
}

// Effects follow where the hero is AIMING, not the body still turning toward it — core's own `wantYaw`,
// offline only (a real `Hero` has it; the wire never sends it — see protocol.ts's HeroSnapshot header on
// why `yaw` alone is the honest online value, since wantYaw is an internal simulation lookahead, not
// something anyone but core needs). Read through a loose cast rather than adding it to ViewHero: online
// simply falls back to the real, already-correct `yaw` — no worse than every other hero-facing effect
// already is, and not a fabricated value standing in for something the server actually knows.
const yawOf = (h: ViewHero) => (h as unknown as { wantYaw?: number }).wantYaw ?? h.yaw;

/* Per-hero/per-projectile throttle and animation-progress latches. Faithful to the prototype, which
   wrote these straight onto the live hero/projectile objects — but those are core state, and a future
   server snapshot can hand the view a fresh object each tick, silently resetting an `as any` field with
   it (a corpse would spawn a new death-pool blot every frame, dust/echo would lose their throttle).
   Keyed by object identity in a WeakMap instead: still per-instance, but owned by the view, not core. */
type HeroFxState = {
  dustAt?: number; echoAt?: number; dying?: boolean; poolAt?: number; hasteAt?: number; hotAt?: number; burnAt?: number;
  /** Mask look last applied (setMaskLook) and the rig content it went onto — a model mounting later needs it again. */
  lookKey?: string; lookOn?: THREE.Object3D;
  invisible?: boolean; flock?: boolean;
  /** When the viewer first saw this hero tied by Связка: the cord shows once the thrown coil lands. */
  tiedAt?: number;
  /** Where the viewer last saw this hero, and on which frame: Тень на стене's departure puff. */
  seenAt?: { x: number; z: number }; seenFrame?: number;
};
type TrailState = { trailAt?: number };

/** `game` accepts a raw core `Game` (main.ts's own offline call site, untouched by this task) as well as a
    `GameView` (the online caller, net/onlineMatch.ts) — normalized to a view immediately below. This is
    the one seam that keeps main.ts's existing call site compiling unchanged while every line below reads
    through the GameView/ViewHero contract exactly like heroView/hud/input/sound already do. */
export function createFx(scene: THREE.Scene, game: Game | GameView, sound: Sound | null = null) {
  const view: GameView = game instanceof Game ? new LocalGameView(game) : game;
  const live: any[] = [];
  const free: Record<string, any[]> = {};
  const BLOTS = [1, 2, 3, 4].map(blotGeometry);
  const heroFx = new WeakMap<ViewHero, HeroFxState>();
  const fxOf = (h: ViewHero): HeroFxState => { let s = heroFx.get(h); if (!s) { s = {}; heroFx.set(h, s); } return s; };
  const trailFx = new WeakMap<object, TrailState>();
  const trailOf = (p: object): TrailState => { let s = trailFx.get(p); if (!s) { s = {}; trailFx.set(p, s); } return s; };

  /* A transient item: build once per key, reuse after it expires. */
  function spawn(key: string, build: () => THREE.Object3D, dur: number, init: (it: any) => void, tick?: (it: any, k: number) => void) {
    const list = free[key] || (free[key] = []);
    const it = list.pop() || { key, obj: build() };
    if (!it.obj.parent) scene.add(it.obj);
    it.obj.visible = true;
    it.t0 = view.t; it.dur = dur; it.tick = tick;
    init(it);
    live.push(it);
    return it;
  }
  const fxMesh = (geo: THREE.BufferGeometry, color: number, opts: Record<string, any> = {}) => mk(geo, color, { fx: true, cast: false, ...opts });
  const smokeMesh = (color: number) => mk(new THREE.IcosahedronGeometry(0.22, 1), color, { cast: false });
  const unitVisible = (h: ViewHero) => view.visibleTo(view.player!.team, h);
  const posVisible = (p: { x: number; z: number }) => view.inSight(view.player!.team, p);
  const BOLTS = createBolts({ spawn, puff, fxMesh, crowMesh, ink: INK, crow: CROW });
  // `look` comes off the wire: an own key only, never an inherited one like 'constructor'
  const boltOf = (look: string | undefined) => (look && Object.hasOwn(BOLTS, look) ? BOLTS[look] : undefined);
  const IMPACTS = createImpacts({ spawn, puff, fxMesh, ink: INK, crow: CROW });
  const impactOf = (by: unknown) => { const k = wireHitBy(by); return k && Object.hasOwn(IMPACTS, k) ? IMPACTS[k] : undefined; };
  const hitAt = new THREE.Vector3(), hitDir = new THREE.Vector3();
  // The impacts drawn in the last IMPACT_GAP per attacker kind (keys: IMPACTS' only). A channel (Саламандра's
  // flame ticks every 0.15 s) or a zone ticking on its victims would otherwise pile impacts over each until he
  // is lost under them. Blows landing in the same frame (one AoE on several heroes) are all drawn.
  const recentImpacts = new Map<string, { t: number; x: number; z: number }[]>();
  const IMPACT_GAP = 0.3, IMPACT_NEAR = 1.2;
  function impactDue(by: string, pos: { x: number; z: number }) {
    let list = recentImpacts.get(by);
    if (!list) recentImpacts.set(by, list = []);
    // `abs`: a replay seeking backwards must not leave stale entries blocking the spot
    for (let i = list.length - 1; i >= 0; i--) if (Math.abs(view.t - list[i]!.t) >= IMPACT_GAP) list.splice(i, 1);
    if (list.some(l => l.t !== view.t && Math.hypot(pos.x - l.x, pos.z - l.z) < IMPACT_NEAR)) return false;
    list.push({ t: view.t, x: pos.x, z: pos.z });
    return true;
  }
  /* The way a blow by `by` travelled into `pos`: from the nearest such attacker in sight within reach of it,
     else any way at all (the event names only the attacker's kind). */
  function blowDir(by: string, pos: { x: number; z: number }, out: THREE.Vector3) {
    let best = 9;
    out.set(0, 0, 0);
    const near = (p: { x: number; z: number }) => {
      const d = Math.hypot(pos.x - p.x, pos.z - p.z);
      if (d > 0.3 && d < best) { best = d; out.set(pos.x - p.x, 0, pos.z - p.z); }
    };
    for (const h of view.heroes) if (h.kind === by && h.alive && unitVisible(h)) near(h.pos);
    for (const s of view.summons) if (s.kind === by) near(s.pos);
    for (const m of view.monsters) if (m.kind === by) near(m.pos);
    if (best < 9) return out.normalize();
    const a = Math.random() * Math.PI * 2;
    return out.set(Math.cos(a), 0, Math.sin(a));
  }

  /* ---------------- transient effects ---------------- */
  function puff(pos: THREE.Vector3, color: number, { size = 1, rise = 0.8, dur = 0.6, drift = null as THREE.Vector3 | null } = {}) {
    spawn('puff' + color, () => smokeMesh(color), dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(pos); it.size = size; it.rise = rise;
      it.drift ||= new THREE.Vector3();
      if (drift) it.drift.copy(drift); else it.drift.set((Math.random() - 0.5) * 0.6, 0, (Math.random() - 0.5) * 0.6);
    }, (it, k) => {
      it.obj.position.copy(it.p).addScaledVector(it.drift, k);
      it.obj.position.y += it.rise * k;
      it.obj.scale.setScalar(Math.max(0.01, it.size * (0.6 + k * 1.2) * (1 - k * k)));
    });
  }

  /* Связка's coil at `k` (0..1) of its flight: a lob from the hand onto the spot. */
  const coilAt = (it: any, k: number, out: THREE.Vector3) =>
    out.lerpVectors(it.from, it.to, k).setY(it.from.y + (it.to.y - it.from.y) * k + it.peak * 4 * k * (1 - k));
  const UP = new THREE.Vector3(0, 1, 0), cordA = new THREE.Vector3(), cordB = new THREE.Vector3();
  /* The rig's right hand, or chest height over the hero before a model with one has mounted. */
  const handOf = (h: ViewHero, out: THREE.Vector3) => {
    const hand = rootOfNow?.(h).getObjectByName('hand.R');
    return hand ? hand.getWorldPosition(out) : out.set(h.pos.x, 1.3 + h.lift, h.pos.z);
  };
  let rootOfNow: ((h: ViewHero) => THREE.Object3D) | null = null;
  // Signature skills', artefacts' and relics' instants (fxSkills.ts) join the handlers below.
  const SKILL_FX = createSkillFx({ spawn, puff, fxMesh, ink: INK, handOf });
  // Summons, Связка's cinch, a point taken, and the births of what zones() draws (fxMoments.ts).
  const MOMENTS = createMomentFx({
    spawn, puff, fxMesh, ink: INK, cordDelay: BIND_FLIGHT,
    ownerAt: (p) => view.points.find(q => Math.hypot(q.pos.x - p.x, q.pos.z - p.z) < 1)?.owner ?? null,
  });
  /* A pool, trap, board or reveal has no event for being made, so its birth is read off its own clock: drawn once,
     only while its age is under BIRTH_WINDOW — one first seen older (out of fog, a late join) never bursts. A key
     lives exactly as long as its entity is listed: online the age only moves with snapshots, so a stalled feed
     (match over, a disconnect) keeps a young entity young for as long as it stays on screen. */
  const BIRTH_WINDOW = 0.15;
  const births = new Set<string>(), birthsListed = new Set<string>();
  function born(key: string, age: number) {
    if (births.has(key)) { birthsListed.add(key); return false; }
    if (!(age >= 0 && age <= BIRTH_WINDOW)) return false;
    births.add(key); birthsListed.add(key);
    return true;
  }

  const handlers: Record<string, (e: any) => void> = {
    hit({ pos, color, big, by }) {
      const impact = impactOf(by);
      if (impact) {
        if (impactDue(by, pos)) impact(hitAt.set(pos.x, 0, pos.z), blowDir(by, pos, hitDir), !!big, Number.isFinite(color) ? color : INK);
        return;
      }
      spawn('ring' + color, () => flat(fxMesh(new THREE.RingGeometry(0.8, 1, 36), color), 0.3), 0.35, (it) => {
        it.obj.position.set(pos.x, 0.3, pos.z); it.s = big ? 1.4 : 1;
      }, (it, k) => it.obj.scale.setScalar((0.3 + k * 1.6) * it.s));
      spawn('flash' + color, () => fxMesh(new THREE.IcosahedronGeometry(0.5, 0), color), 0.2, (it) => {
        it.obj.position.set(pos.x, 1.2, pos.z); it.s = big ? 1.5 : 1;
      }, (it, k) => { it.obj.scale.setScalar(Math.max(0.01, (1 - k) * it.s)); it.obj.rotation.set(k * 6, k * 4, 0); });
    },
    splat({ pos, big }) {
      const geo = BLOTS[Math.floor(Math.random() * BLOTS.length)] as THREE.ShapeGeometry;
      spawn('blot' + geo.uuid, () => flat(fxMesh(geo, INK), 0.23), 2.6, (it) => {
        it.obj.position.set(pos.x + (Math.random() - 0.5) * 0.4, 0.23 + Math.random() * 0.01, pos.z + (Math.random() - 0.5) * 0.4);
        it.obj.rotation.z = Math.random() * 6; it.s = big ? 1.7 : 1.1;
      }, (it, k) => it.obj.scale.setScalar(it.s * Math.min(1, k * 12) * (k > 0.75 ? 1 - (k - 0.75) * 4 : 1) + 0.001));
    },
    slash({ hero, flip }: { hero: ViewHero; flip: number }) {  // melee: every character's blade leaves its own mark
      const st = SLASH[hero.kind] || SLASH.rhino;
      const arcs = st.double ? [0, 1] : [0];
      for (const n of arcs) {
        spawn('slash' + hero.kind + n, () => flat(fxMesh(new THREE.RingGeometry(st.inner, st.outer, 40, 1, 0, st.arc), st.color), 0.4), st.dur, (it) => {
          it.h = hero; it.flip = n ? !flip : flip; it.tilt = n ? 0.5 : 0;
        }, (it, k) => {
          const h = it.h, sweep = (it.flip ? -1 : 1) * (k - 0.5) * 0.9;
          it.obj.position.set(h.pos.x, st.y + it.tilt * 0.4, h.pos.z);
          it.obj.rotation.set(-Math.PI / 2 + it.tilt * 0.3, 0, yawOf(h) - Math.PI / 2 - st.arc / 2 + sweep);
          it.obj.scale.setScalar((0.85 + k * 0.3) * (1 - k * k * 0.9) + 0.001);
        });
      }
      const f = new THREE.Vector3(Math.sin(yawOf(hero)), 0, Math.cos(yawOf(hero))), at = hero.pos.clone().addScaledVector(f, st.outer * 0.8);
      if (st.dust) for (let i = 0; i < 2; i++) puff(at.clone().setY(0.2), 0xcbbf9f, { size: 0.8, rise: 0.3, dur: 0.5 });
      if (st.splat) (handlers.splat as any)({ pos: at });
      if (st.embers) for (let i = 0; i < 3; i++) puff(at.clone().setY(1), 0x7a2a20, { size: 0.5, rise: 0.8, dur: 0.5 });
    },
    muzzle({ hero, big }: { hero: ViewHero; big?: boolean }) {
      const f = new THREE.Vector3(Math.sin(yawOf(hero)), 0, Math.cos(yawOf(hero)));
      const at = hero.pos.clone().addScaledVector(f, 1.1).add(new THREE.Vector3(0, 1.25, 0));
      const look = hero.def.boltLook;
      const bolt = boltOf(look);
      if (bolt) { bolt.muzzle(at, f, !!big, hero.def.bolt ?? bolt.color); return; }
      if (look === 'smoke' || look === 'spit') {  // censer / grotesque: a waft, no flash
        puff(at, look === 'spit' ? 0x8a9460 : 0xb4c4a0, { size: 0.9, rise: 0.4, dur: 0.7, drift: f.clone().multiplyScalar(0.8) });
        return;
      }
      if (look === 'mote') { (handlers.hit as any)({ pos: at.clone().setY(0), color: 0xf6e8a0, big: false }); return; }
      if (look === 'star') {
        for (let i = 0; i < 4; i++) spawn('twinkle', () => fxMesh(new THREE.OctahedronGeometry(0.1), 0xd8e0ff), 0.35, (it) => {
          it.p = at.clone(); it.v = new THREE.Vector3((Math.random() - 0.5) * 3, Math.random() * 2, (Math.random() - 0.5) * 3);
        }, (it, k) => { it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.35); it.obj.scale.setScalar(1 - k + 0.01); });
        if (!big) return;
      }
      const smoke = look === 'ember' ? 0x6a6058 : 0xd0c7b3, flash = look === 'ember' ? 0xff7a2c : look === 'star' ? 0xc8d4ff : 0xffc070;
      for (let i = 0; i < (big ? 5 : 3); i++) puff(at, smoke, { size: big ? 1.3 : 0.9, rise: 0.6, dur: big ? 1.0 : 0.7, drift: f.clone().multiplyScalar(0.6 + Math.random()) });
      spawn('mflash' + flash, () => fxMesh(new THREE.ConeGeometry(0.3, 0.9, 6), flash), 0.08, (it) => {
        it.obj.position.copy(at).addScaledVector(f, 0.3);
        it.obj.lookAt(at.clone().addScaledVector(f, 2)); it.obj.rotateX(Math.PI / 2); it.s = big ? 1.8 : 1;
      }, (it, k) => it.obj.scale.setScalar(it.s * (1 - k) + 0.001));
    },
    dust({ hero }: { hero: ViewHero }) {
      const s = fxOf(hero);
      if (view.t - (s.dustAt || 0) < 0.05) return;
      s.dustAt = view.t;
      const back = new THREE.Vector3(-Math.sin(yawOf(hero)), 0, -Math.cos(yawOf(hero)));
      puff(hero.pos.clone().addScaledVector(back, 0.6).add(new THREE.Vector3(0, 0.2, 0)), 0xcbbf9f, { size: 1, rise: 0.4, dur: 0.45 });
    },
    echo({ hero }: { hero: ViewHero }) {  // dash streaks: short ink smears on the ground along the motion, fading fast
      const s = fxOf(hero);
      if (view.t - (s.echoAt || 0) < 0.04) return;
      s.echoAt = view.t;
      const geo = BLOTS[Math.floor(Math.random() * BLOTS.length)] as THREE.ShapeGeometry;
      spawn('streak' + geo.uuid, () => flat(fxMesh(geo, 0x3b3040), 0.25), 0.45, (it) => {
        it.obj.position.set(hero.pos.x + (Math.random() - 0.5) * 0.5, 0.25, hero.pos.z + (Math.random() - 0.5) * 0.5);
        it.obj.rotation.z = -yawOf(hero) + Math.PI / 2;
      }, (it, k) => it.obj.scale.set(0.9 * (1 - k) + 0.01, 0.35 * (1 - k) + 0.01, 1));
    },
    cut({ from, to }: { from: { x: number; z: number }; to: { x: number; z: number } }) {
      spawn('cut', () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), INK), 0.24), 1.3, (it) => {
        const dx = to.x - from.x, dz = to.z - from.z, len = Math.max(0.5, Math.hypot(dx, dz));
        it.obj.position.set((from.x + to.x) / 2, 0.24, (from.z + to.z) / 2);
        it.obj.rotation.set(-Math.PI / 2, 0, Math.atan2(-dz, dx));
        it.len = len;
      }, (it, k) => it.obj.scale.set(it.len * (k < 0.1 ? k * 10 : 1), 0.28 * (1 - k) + 0.001, 1));
    },
    // Связка thrown: a coil of cord lobs from the Кукольник's hand onto the spot, paying out cord behind it
    bindThrow({ hero, to }: { hero: ViewHero; to: unknown }) {
      if (!isVec(to)) return;
      const from = handOf(hero, new THREE.Vector3()), end = new THREE.Vector3(to.x, 0.12, to.z);
      const peak = 0.6 + Math.hypot(end.x - from.x, end.z - from.z) * 0.1;
      spawn('bindCoil', () => fxMesh(new THREE.TorusGeometry(0.17, 0.045, 6, 16), CORD), BIND_FLIGHT, (it) => {
        it.from = from; it.to = end; it.peak = peak;
      }, (it, k) => { coilAt(it, k, it.obj.position); it.obj.rotation.set(k * 14, k * 6, 0); });
      // the cord stays tied to the hand while the coil flies, then lies there a moment before the ties take over
      spawn('bindCord', () => fxMesh(new THREE.CylinderGeometry(0.02, 0.02, 1, 5), CORD), BIND_FLIGHT + 0.15, (it) => {
        it.from = from; it.to = end; it.peak = peak; it.h = hero;
      }, (it, k) => {
        const flown = Math.min(1, k * it.dur / BIND_FLIGHT);
        handOf(it.h, cordA);
        coilAt(it, flown, cordB);
        const len = cordA.distanceTo(cordB);
        it.obj.position.lerpVectors(cordA, cordB, 0.5);
        if (len > 1e-4) it.obj.quaternion.setFromUnitVectors(UP, cordB.sub(cordA).divideScalar(len));
        it.obj.scale.set(flown < 1 ? 1 : 0.5, Math.max(0.001, len), flown < 1 ? 1 : 0.5);
      });
    },
    // …and lands: the catch circle cinches in to the skill's reach around the spot
    bind({ pos }: { pos: { x: number; z: number } }) {
      spawn('bindRing', () => flat(fxMesh(new THREE.RingGeometry(0.93, 1, 48), CORD), 0.3), BIND_FLIGHT + 0.45, (it) => {
        it.obj.position.set(pos.x, 0.3, pos.z);
      }, (it, k) => {
        const t = (k * it.dur - BIND_FLIGHT) / 0.45;
        it.obj.scale.setScalar(t < 0 ? 0.001 : SKILLS.puppeteer.W.r * (1.3 - 0.3 * Math.min(1, t * 3)));
      });
    },
    shockwave({ pos, color = 0xf2d46b, big }: { pos: { x: number; z: number }; color?: number; big?: boolean }) {
      spawn('wave' + color, () => flat(fxMesh(new THREE.RingGeometry(0.85, 1, 48), color), 0.32), 0.4, (it) => {
        it.obj.position.set(pos.x, 0.32, pos.z); it.s = big ? 3.6 : 2.6;
      }, (it, k) => it.obj.scale.setScalar(0.3 + k * it.s));
    },
    block({ pos, yaw }: { pos: { x: number; z: number }; yaw: number }) {
      const f = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      for (let i = 0; i < 5; i++) {
        spawn('spark', () => fxMesh(new THREE.OctahedronGeometry(0.12), 0xf6dc78), 0.25, (it) => {
          it.p = new THREE.Vector3(pos.x, 1.3, pos.z).addScaledVector(f, 0.9);
          it.v = f.clone().multiplyScalar(2 + Math.random() * 2).add(new THREE.Vector3((Math.random() - 0.5) * 4, Math.random() * 3, (Math.random() - 0.5) * 4));
        }, (it, k) => { it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.25); it.obj.scale.setScalar(1 - k + 0.01); });
      }
    },
    feintArmed({ hero }: { hero: ViewHero }) {
      spawn('glint', () => flat(fxMesh(new THREE.RingGeometry(1.0, 1.25, 40, 1, 0, 3.4), 0xd6b8f0), 1.2), 0.35, (it) => { it.h = hero; },
        (it, k) => { it.obj.position.set(it.h.pos.x, 1.2, it.h.pos.z); it.obj.rotation.z = k * 4; it.obj.scale.setScalar(0.6 + k); });
    },
    shatter({ pos }: { pos: { x: number; z: number } }) {
      (handlers.hit as any)({ pos, color: 0x9fe07a, big: true });
      for (let i = 0; i < 8; i++) {
        const a = i / 8 * Math.PI * 2 + Math.random();
        spawn('glass', () => fxMesh(new THREE.TetrahedronGeometry(0.12), 0xd8f2e0), 0.5, (it) => {
          it.p = new THREE.Vector3(pos.x, 0.6, pos.z); it.v = new THREE.Vector3(Math.cos(a) * 3.5, 2.5, Math.sin(a) * 3.5);
        }, (it, k) => { it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.5); it.obj.position.y += -3 * k * k; it.obj.rotation.set(k * 12, k * 9, 0); });
      }
    },
    trapPop({ pos }: { pos: { x: number; z: number } }) {
      (handlers.hit as any)({ pos, color: 0xd2d86a, big: true });
      for (let i = 0; i < 4; i++) puff(new THREE.Vector3(pos.x, 0.4, pos.z), 0xbcc488, { size: 1.4, rise: 0.8, dur: 1.1 });
    },
    feint() {},
    sweep({ hero, dir }: { hero: ViewHero; dir: { x: number; z: number } }) {  // Размах: a wide half-moon of ink
      const d = new THREE.Vector3(dir.x, 0, dir.z);
      spawn('sweep', () => flat(fxMesh(new THREE.RingGeometry(1.4, 3.3, 40, 1, 0, Math.PI), INK), 0.42), 0.3, (it) => {
        it.p = hero.pos.clone(); it.a = Math.atan2(d.x, d.z);
      }, (it, k) => {
        it.obj.position.set(it.p.x, 0.42, it.p.z);
        it.obj.rotation.set(-Math.PI / 2, 0, it.a - Math.PI);
        it.obj.scale.setScalar((0.8 + k * 0.3) * (1 - k * k) + 0.001);
      });
      for (let i = 0; i < 3; i++) puff(hero.pos.clone().addScaledVector(d, 2).add(new THREE.Vector3(0, 0.3, 0)), 0xcbbf9f, { size: 1.1, rise: 0.3, dur: 0.6 });
    },
    taunt({ hero }: { hero: ViewHero }) { (handlers.hit as any)({ pos: hero.pos, color: 0xe0604a, big: false }); },
    prayer({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 6; i++) {
        spawn('mote', () => fxMesh(new THREE.OctahedronGeometry(0.1), 0xf6e8a0), 0.9, (it) => {
          it.h = hero; it.a = i / 6 * Math.PI * 2;
        }, (it, k) => {
          const a = it.a + k * 3;
          it.obj.position.set(it.h.pos.x + Math.cos(a) * 0.7, 0.3 + k * 2.2, it.h.pos.z + Math.sin(a) * 0.7);
          it.obj.scale.setScalar(1 - k + 0.01);
        });
      }
    },
    flame({ hero, dir }: { hero: ViewHero; dir: { x: number; z: number } }) {  // Огненная струя: embers stream out along the cone
      const d0 = new THREE.Vector3(dir.x, 0, dir.z);
      const at = hero.pos.clone().addScaledVector(d0, 0.9).add(new THREE.Vector3(0, 1.2, 0));
      for (let i = 0; i < 3; i++) {
        const spread = (Math.random() - 0.5) * 0.9, d = new THREE.Vector3(d0.x * Math.cos(spread) + d0.z * Math.sin(spread), 0, -d0.x * Math.sin(spread) + d0.z * Math.cos(spread));
        spawn('ember', () => fxMesh(new THREE.IcosahedronGeometry(0.22, 0), 0xff8a3c), 0.4, (it) => {
          it.p = at.clone(); it.v = d.multiplyScalar(11);
        }, (it, k) => {
          it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.4); it.obj.position.y += -0.9 * k;
          it.obj.scale.setScalar(0.6 + k * 1.6);
        });
      }
      puff(at.clone().addScaledVector(d0, 2.5), 0x9a9080, { size: 1, rise: 0.8, dur: 0.7 });
    },

    /* ---- artefacts (docs/game-design.md, "Артефакты" — flat, single-tier: no upgrades, and Игла
       гравёра/Зерцало are gone along with their state and fx events). The lasting parts of each still-live
       line (the cloak's ghost contour, the tar pool, the totems) are drawn continuously in
       statuses()/zones() above instead; these are only the instant something happens. */
    bloodvial({ hero }: { hero: ViewHero }) {  // Склянка крови: a draught, a warm green pulse
      (handlers.hit as any)({ pos: hero.pos, color: 0x9fe07a, big: false });
      puff(hero.pos.clone().add(new THREE.Vector3(0, 0.6, 0)), 0xd8f2c8, { size: 0.9, rise: 0.6, dur: 0.6 });
    },
    cloak({ hero }: { hero: ViewHero }) {  // vanishing into the ghost contour — a paper-pale puff, no ink
      puff(hero.pos.clone().add(new THREE.Vector3(0, 0.6, 0)), 0xd8d0bc, { size: 1.1, rise: 0.9, dur: 0.6 });
    },
    totemPlace({ pos, kind }: { pos: { x: number; z: number }; kind: 'ward' | 'wrath' }) {  // driven into the ground, a plain thud
      for (let i = 0; i < 3; i++) puff(new THREE.Vector3(pos.x, 0.3, pos.z), 0xbcae94, { size: 0.9, rise: 0.4, dur: 0.6 });
      (handlers.shockwave as any)({ pos, color: TOTEM_LOOK[kind].ring });
    },
    totemBreak({ pos, kind }: { pos: { x: number; z: number }; kind: 'ward' | 'wrath' }) {  // its second hit — splinters, not glass or ink
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * Math.PI * 2;
        spawn('splinter' + kind, () => fxMesh(new THREE.ConeGeometry(0.05, 0.5, 3), TOTEM_LOOK[kind].wood), 0.5, (it) => {
          it.p = new THREE.Vector3(pos.x, 1.2, pos.z); it.v = new THREE.Vector3(Math.cos(a) * 3, 2.5, Math.sin(a) * 3);
        }, (it, k) => { it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.5); it.obj.position.y += -3 * k * k; it.obj.rotation.set(k * 6, 0, k * 9); });
      }
    },
    brand({ hero }: { hero: ViewHero }) {  // the seal taking
      (handlers.hit as any)({ pos: hero.pos, color: 0xc8501e, big: false });
    },
    brandMonster({ pos }: { pos: { x: number; z: number } }) {
      (handlers.hit as any)({ pos, color: 0xc8501e, big: false });
    },

    /* ---- Астрал. Lasting parts (Тень's shade, the charm thread, the flock, the marks) live in statuses()/
       zones(); these are the instants. Only her own team ever gets `stealth` (dispatch below). ---- */
    stealth({ hero }: { hero?: ViewHero }) { if (hero) dusk(hero.pos); },
    shadowSwap({ hero, pos }: { hero?: ViewHero; pos?: { x: number; z: number } }) {
      if (!hero || !isVec(pos)) return;
      // Departure: only if the viewer actually saw her there on the frame before — never a fogged spot.
      const s = fxOf(hero), from = s.seenAt;
      if (from && s.seenFrame === frame - 1 && Math.hypot(from.x - pos.x, from.z - pos.z) > 0.5) dusk(from);
      dusk(pos);
      (handlers.splat as any)({ pos });
    },
    ...SKILL_FX,
    ...MOMENTS.events,
  };

  /* Слиться's way in and out, and both ends of Тень на стене: dusk-coloured smoke closing on a spot. */
  function dusk(at: { x: number; z: number }) {
    for (let i = 0; i < 4; i++) {
      const a = i / 4 * Math.PI * 2 + Math.random();
      puff(new THREE.Vector3(at.x + Math.cos(a) * 0.7, 0.5, at.z + Math.sin(a) * 0.7), DUSK,
        { size: 1, rise: 0.7, dur: 0.6, drift: new THREE.Vector3(-Math.cos(a) * 0.9, 0, -Math.sin(a) * 0.9) });
    }
  }
  /* Расплата sharing a blow: a brief crimson stroke from the struck ally to Грех. */
  function link(from: { x: number; z: number }, to: { x: number; z: number }) {
    spawn('link', () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), CRIMSON), 1), 0.45, (it) => {
      const dx = to.x - from.x, dz = to.z - from.z;
      it.len = Math.max(0.3, Math.hypot(dx, dz));
      it.obj.position.set((from.x + to.x) / 2, 1, (from.z + to.z) / 2);
      it.obj.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) + Math.PI);
    }, (it, k) => it.obj.scale.set(0.14 * (1 - k) + 0.001, it.len * Math.min(1, k * 6), 1));
  }

  /* ---------------- state-driven visuals ---------------- */
  const keyed = new Map<any, THREE.Object3D & { userData: { touched: number } }>();  // owner object → visual group
  // Owners whose visual is freed (not just hidden) once a frame passes without it: every object owner
  // (projectiles, pools, traps…) plus string keys that embed a one-off entity id (a lob's landing ring).
  const oneOff = new Set<any>();
  function visualFor(owner: any, build: () => THREE.Object3D, once = typeof owner === 'object') {
    let v = keyed.get(owner);
    if (!v) {
      v = build() as any; (v as any).userData.touched = 0; keyed.set(owner, v as any); scene.add(v as any);
      if (once) oneOff.add(owner);
    }
    (v as any).userData.touched = frame;
    (v as any).visible = true;
    return v as any;
  }
  let frame = 0;

  // Per-frame scratch: never held past the call that fills it (puff/trail copy what they keep).
  const P3 = new THREE.Vector3(), AIM = new THREE.Vector3(), LOOK = new THREE.Vector3();
  const headings = createHeadings();
  function projectileVisual(p: ViewProjectile) {
    const tr = trailOf(p);
    const look = p.boltLook ?? 'lance';
    const bolt = boltOf(look);
    const v = visualFor(p, () => {
      const g = new THREE.Group();
      if (bolt) {
        bolt.build(g, p.color ?? bolt.color);
      } else if (look === 'smoke') {
        for (let i = 0; i < 3; i++) { const m = smokeMesh(0xa9b89a); (m as any).userData.k = i; g.add(m); }
      } else if (look === 'star') {
        const star = fxMesh(new THREE.OctahedronGeometry(0.28), 0xd8e0ff);
        star.scale.set(1, 0.5, 1);
        g.add(star);
      } else if (look === 'ember') {
        g.add(fxMesh(new THREE.IcosahedronGeometry(0.3, 0), 0xff7a2c));
      } else if (look === 'mote') {
        g.add(fxMesh(new THREE.OctahedronGeometry(0.22), 0xf6e8a0));
      } else if (look === 'spit') {
        const b = fxMesh(new THREE.SphereGeometry(0.22, 10, 8), 0x8a9a50);
        b.scale.set(1, 0.8, 1.5);
        g.add(b);
      } else if (look === 'hook') {
        g.add(fxMesh(new THREE.TorusGeometry(0.28, 0.06, 6, 16, Math.PI * 1.3), 0x9a9690));
      } else if (look === 'falcon') {  // Сокол на перчатке: a stooping bird, long wings swept back (birdFigures.ts)
        g.add(buildFalcon());
      } else {
        // A musket shot: a dark lead ball leading a hot tracer that tapers away BEHIND it (+z is forward
        // once the group is turned by its heading); the charged 'lance' is a heavier ball, a longer, brighter
        // tracer with a white-hot core.
        const big = look === 'lance';
        const r = big ? 0.2 : 0.13, len = big ? 2.6 : 1.3, w = big ? 0.15 : 0.09;
        g.add(mk(new THREE.SphereGeometry(r, 10, 8), 0x2e2a26, { cast: false }));
        const tracer = (tw: number, tlen: number, color: number) => {
          const m = fxMesh(new THREE.CylinderGeometry(tw, 0, tlen, 6), color);
          m.rotation.x = Math.PI / 2;
          m.position.z = -tlen / 2 - r * 0.8;
          g.add(m);
        };
        tracer(w, len, big ? 0xff9a3c : 0xffc070);
        if (big) tracer(w * 0.45, len * 0.55, 0xfff0c8);
      }
      (g as any).userData.look = look;
      return g;
    });
    // `p.dir` only ever exists for a 'linear' shot (see ViewProjectile's own header) — its presence IS the
    // kind discriminant, and doubles for the one other thing core's own `kind` used to gate: the fixed
    // spawn height (spawnLinear's 1.3 vs homing's 1.5 — never updated after spawn, so a constant here loses
    // nothing `p.pos.y` itself would have said). The falcon is the one exception: `p.arc` (real on the wire
    // too, unlike `dir`/`targetPos`) drives a dedicated climb-and-dive height instead of a constant.
    const pos3 = P3.set(p.pos.x, look === 'falcon' ? falconHeight(p.arc ?? 0) : p.dir ? 1.3 : 1.5, p.pos.z);
    v.position.copy(pos3);
    // Heading from the shot's aim offline, from its own motion online/in replays (the wire has no aim).
    const aim = p.dir ?? (p.targetPos ? AIM.set(p.targetPos.x - p.pos.x, 0, p.targetPos.z - p.pos.z) : null);
    const yaw = headings.heading(p.id, p.pos, aim);
    if (yaw !== null) v.rotation.set(0, yaw, 0);
    if (look === 'falcon') v.rotation.x = falconPitch(p.arc ?? 0);  // nose up climbing, down stooping — same envelope as the height
    if (bolt) {
      bolt.tick?.(v, view.t);
      // no heading yet (first online frame): nothing to drop it behind
      if (yaw !== null && view.t - (tr.trailAt || 0) > bolt.every) { tr.trailAt = view.t; bolt.trail(pos3, yaw, p.color ?? bolt.color); }
      return;
    }
    if (look === 'smoke') {
      for (let i = 0; i < v.children.length; i++) {
        const m = v.children[i], w = view.t * 6 + i * 2.1;
        m.position.set(Math.sin(w) * 0.25, Math.cos(w * 1.3) * 0.15, -i * 0.35);
        m.scale.setScalar(1.1 - i * 0.2 + Math.sin(w) * 0.1);
      }
      if (view.t - (tr.trailAt || 0) > 0.07) { tr.trailAt = view.t; puff(pos3, 0xb4c4a0, { size: 0.5, rise: 0.25, dur: 0.5 }); }
    } else {
      if (look === 'star' || look === 'mote') {
        v.children[0].rotation.y = view.t * 12;
        if (view.t - (tr.trailAt || 0) > 0.05) {
          tr.trailAt = view.t;
          const c = look === 'star' ? 0xd8e0ff : 0xf6e8a0;
          spawn('trail' + c, () => fxMesh(new THREE.OctahedronGeometry(0.08), c), 0.4, (it) => { (it.p ||= new THREE.Vector3()).copy(pos3); }, (it, k) => {
            it.obj.position.copy(it.p); it.obj.position.y += -k * 0.3; it.obj.scale.setScalar(1 - k + 0.01);
          });
        }
        return;
      }
      if (look === 'ember') {
        v.children[0].rotation.set(view.t * 9, view.t * 7, 0);
        if (view.t - (tr.trailAt || 0) > 0.04) { tr.trailAt = view.t; puff(pos3, 0x5a524c, { size: 0.55, rise: 0.5, dur: 0.6 }); }
        return;
      }
      if (look === 'spit') {
        if (view.t - (tr.trailAt || 0) > 0.06) { tr.trailAt = view.t; puff(pos3, 0x8a9460, { size: 0.4, rise: -0.3, dur: 0.5 }); }
        return;
      }
      // The chain endpoint is the SHOOTER's position, keyed by shooter id — offline only (`srcId`/`p.pos`
      // as the far end is real online too, but with no shooter position to anchor the near end to, online
      // simply never draws this chain, matching remoteEntities.ts's own existing simplification).
      if (look === 'hook') { if (p.srcId !== undefined) chain('p' + p.srcId, view.heroes[p.srcId]!.pos, pos3); return; }
      if (look === 'falcon') return;  // no powder smoke behind a bird
      // A thin powder-smoke wake left a little behind the ball, never on top of it.
      if (view.t - (tr.trailAt || 0) > (look === 'lance' ? 0.02 : 0.03)) {
        tr.trailAt = view.t;
        if (yaw !== null) pos3.set(pos3.x - Math.sin(yaw) * 0.5, pos3.y, pos3.z - Math.cos(yaw) * 0.5);
        puff(pos3, 0xd0c7b3, { size: look === 'lance' ? 0.7 : 0.4, rise: 0.15, dur: look === 'lance' ? 0.8 : 0.4 });
      }
    }
  }

  /* A chain from the ferryman to his hook (or to whoever it is dragging). */
  function chain(id: string, from: THREE.Vector3, to: THREE.Vector3) {
    const v = visualFor('chain:' + id, () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), 0x5a5550), 1.3));
    const dx = to.x - from.x, dz = to.z - from.z, len = Math.hypot(dx, dz);
    v.scale.set(0.12, Math.max(0.01, len), 1);
    v.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) + Math.PI);
    v.position.set((from.x + to.x) / 2, 1.3, (from.z + to.z) / 2);
  }

  function lobVisual(l: ViewLob) {
    if (!l.from || !l.to) return;  // one endpoint fog-hidden — online only, offline's own getter never nulls either
    const v = visualFor(l, () => {
      const g = new THREE.Group();
      const bulb = fxMesh(new THREE.SphereGeometry(0.2, 12, 10), 0xbfe8c8);
      const neck = fxMesh(new THREE.CylinderGeometry(0.06, 0.08, 0.22, 8), 0xbfe8c8);
      neck.position.y = 0.24;
      const cork = mk(new THREE.CylinderGeometry(0.07, 0.07, 0.08, 8), 0x8a6a48, { cast: false });
      cork.position.y = 0.38;
      g.add(bulb, neck, cork);
      return g;
    });
    const k = Math.min(1, (view.t - l.t0) / l.flight);
    v.position.lerpVectors(P3.set(l.from.x, 0.4, l.from.z), LOOK.set(l.to.x, 0.4, l.to.z), k);
    v.position.y += Math.sin(Math.PI * k) * 3.2;
    v.rotation.set(view.t * 11, 0, view.t * 7);
    const ring = visualFor('landing:' + l.id, () => {
      const g = new THREE.Group();
      g.add(flat(fxMesh(new THREE.RingGeometry(l.r - 0.14, l.r, 56), TEAM[l.team]), 0));
      g.add(flat(fxMesh(new THREE.RingGeometry(0.85, 1, 40), TEAM[l.team]), 0));
      return g;
    }, true);
    ring.position.set(l.to.x, 0.33, l.to.z);
    ring.children[1].scale.setScalar(Math.max(0.01, l.r * (1 - k)));
  }

  function telegraphs() {
    for (const h of view.heroes) {
      if (!h.alive || !unitVisible(h)) continue;
      const { dash, hop } = view.telegraphOf(h);
      let len = 0, width = 0, dir: { x: number; z: number } | null = null, color = TEAM[h.team];
      // Online, `state` and `dash` can come from different ticks (own hero: predicted state vs snapshot
      // dash; others: interpolated state vs latest dash), so a dash state with no dash is normal there.
      if ((h.state === 'windup' || h.state === 'dash') && dash) { len = dash.left; width = 1.2; dir = dash.dir; }
      else if (h.state === 'lungeWindup' && dash) { len = dash.left; width = 1.0; dir = dash.dir; color = 0x3b3040; }
      else if (h.state === 'charging') {
        const sk = SKILLS.arquebusier.Q, k = view.chargeLevel(h);
        len = sk.range[0] + (sk.range[1] - sk.range[0]) * k; width = sk.width * (0.25 + 0.45 * k);
        dir = { x: Math.sin(yawOf(h)), z: Math.cos(yawOf(h)) };
      }
      if (h.state === 'leap' && hop) {  // Прыжок: the landing circle, visible to both teams
        const r = SKILLS.devil.Q.r, land = visualFor('leap:' + h.id, () => flat(fxMesh(new THREE.RingGeometry(r - 0.16, r, 48), 0x8a2a28), 0.34));
        land.position.set(hop.to.x, 0.34, hop.to.z);
      }
      const puller = view.tetherSource(h);
      if (puller) chain('h' + h.id, puller.pos, h.pos);
      if (!dir) continue;
      const v = visualFor('tele:' + h.id, () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), 0xffffff), 0.34));
      v.userData.mats.albedo.color.setHex(color);  // fx meshes share one material for albedo and lit
      const a = Math.atan2(dir.x, dir.z);
      v.scale.set(width, len, 1);
      v.rotation.set(-Math.PI / 2, 0, a + Math.PI);
      v.position.set(h.pos.x + dir.x * len / 2, 0.34, h.pos.z + dir.z * len / 2);
    }
  }

  const POOL_LOOK: Record<string, any> = {
    heal: { color: 0x9fe07a, bubble: 0xd8f2c8 },
    fire: { color: 0xff8a3c, bubble: 0xffd08a },
    lantern: { color: 0x9a86c8, ring: true },
    holy: { color: 0xf2d46b, ring: true },
    // Смоляная бутыль: dark tar, same disc-plus-bubbles shape as heal/fire — the design doc gives tar one
    // look across all three tiers (only the slow/heal-reduction/damage NUMBERS change by tier, all
    // server-resolved), so no per-tier variant is drawn here.
    tar: { color: INK, bubble: 0x3a2a1c },
    // Пергаментный плащ's exit blot (tier 3 only) — same shape again, paler ink, no bubbles read as tar.
    blot: { color: 0x5a5550, bubble: 0x5a5550 },
    // Астрал: both follow their caster, so they are rims around him rather than ground stains.
    atonement: { color: CRIMSON, ring: true },
    delusion: { color: BRUISE, ring: true },
  };
  function zones() {
    for (const z of view.pools) {
      if (MOMENTS.births.hasPool(z.look) && born('pool:' + z.id, z.life - z.left)) MOMENTS.births.pool(z);
      const k = 1 - z.left / z.life;
      const grow = saneGrow(Math.min(1, k * 10) * (k > 0.85 ? 1 - (k - 0.85) / 0.15 : 1), 'pool#' + z.id);
      if (z.look === 'cloud') {  // plague cloud: hatched smoke, not a stipple disc
        const v = visualFor(z, () => {
          const g = new THREE.Group();
          for (let i = 0; i < 9; i++) { const m = smokeMesh(0x6f7a50); m.scale.setScalar(3); g.add(m); }
          return g;
        });
        v.position.set(z.pos.x, 0.6, z.pos.z);
        v.children.forEach((m: any, i: number) => {
          const a = i / 9 * Math.PI * 2 + view.t * 0.6, rr = (i % 3 ? 0.65 : 0.2) * z.r;
          m.position.set(Math.cos(a) * rr, Math.sin(view.t * 2 + i) * 0.2 + (i % 2) * 0.4, Math.sin(a) * rr);
          m.scale.setScalar((2.6 + Math.sin(view.t * 3 + i) * 0.4) * grow + 0.01);
        });
        continue;
      }
      if (z.look === 'shadowMark') {  // Тень на стене: her shadow pooled on the ground, its rim ticking full over the delay
        const TICKS = 20;
        const v = visualFor(z, () => {
          const g = new THREE.Group();
          g.add(flat(fxMesh((BLOTS[2] as THREE.ShapeGeometry).clone(), DUSK), 0));  // own copy: sweep() disposes it
          for (let i = 0; i < TICKS; i++) {
            const a = i / TICKS * Math.PI * 2, tick = flat(fxMesh(new THREE.PlaneGeometry(0.07, 0.26), INK), 0.02);
            tick.position.set(Math.sin(a) * 1.25, 0.02, -Math.cos(a) * 1.25);
            tick.rotation.z = -a;
            g.add(tick);
          }
          return g;
        });
        const k = Math.max(0, Math.min(1, 1 - z.left / z.life));
        v.position.set(z.pos.x, 0.27, z.pos.z);
        v.children[0].scale.setScalar(0.5 + 0.5 * Math.min(1, k * 4));
        v.children[0].rotation.z = view.t * 0.8;
        for (let i = 1; i < v.children.length; i++) v.children[i].visible = (i - 1) / TICKS < k;
        v.scale.setScalar(Math.max(0.3, z.r));
        continue;
      }
      const look = POOL_LOOK[z.look ?? ''] || POOL_LOOK.heal;
      const v = visualFor(z, () => {
        const g = new THREE.Group();
        g.add(flat(fxMesh(look.ring ? new THREE.RingGeometry(0.93, 1, 64) : new THREE.CircleGeometry(1, 36), look.color), 0));
        if (look.ring) g.add(flat(fxMesh(new THREE.CircleGeometry(1, 36), look.color), -0.02));
        for (let i = 0; i < 4; i++) { const b = fxMesh(new THREE.SphereGeometry(0.12, 8, 6), look.bubble || look.color); g.add(b); }
        return g;
      });
      v.position.set(z.pos.x, 0.28, z.pos.z);
      v.children[0].scale.setScalar(z.r * grow + 0.001);
      if (look.ring) v.children[1].scale.setScalar(z.r * grow * (0.35 + 0.1 * Math.sin(view.t * 4)) + 0.001);
      for (let i = 0, first = look.ring ? 2 : 1; i < v.children.length - first; i++) {
        const b = v.children[first + i], w = view.t * 2 + i * 1.7;
        b.position.set(Math.cos(i * 1.6) * z.r * 0.5, 0.1 + ((w % 1) * 0.5), Math.sin(i * 1.6) * z.r * 0.5);
        b.scale.setScalar(1 - (w % 1));
      }
    }
    for (const z of view.pools) {  // Святой круг: pillars of light stand on its rim
      if (z.look !== 'holy') continue;
      const v = visualFor('pillars:' + z.id, () => {
        const g = new THREE.Group();
        for (let i = 0; i < 6; i++) g.add(fxMesh(new THREE.CylinderGeometry(0.09, 0.14, 2.4, 6), 0xf6e8a0));
        return g;
      }, true);
      v.userData.touched = frame;
      const k = 1 - z.left / z.life;
      v.position.set(z.pos.x, 0, z.pos.z);
      v.children.forEach((c: any, i: number) => {
        const a = i / 6 * Math.PI * 2 + view.t * 0.5;
        c.position.set(Math.cos(a) * z.r, 1.2, Math.sin(a) * z.r);
        c.scale.set(1, Math.max(0.01, Math.min(1, k * 8) * (k > 0.85 ? (1 - k) / 0.15 : 1)), 1);
      });
    }
    for (const z of view.pools) {  // Наваждение: crows wheel on its rim
      if (z.look !== 'delusion') continue;
      const v = visualFor('crows:' + z.id, () => {
        const g = new THREE.Group();
        for (let i = 0; i < 5; i++) g.add(crowMesh());
        return g;
      }, true);
      v.position.set(z.pos.x, 0, z.pos.z);
      v.children.forEach((c: any, i: number) => {
        const a = i / 5 * Math.PI * 2 + view.t * 0.9;
        c.position.set(Math.cos(a) * z.r, 1.6 + Math.sin(view.t * 3 + i) * 0.25, Math.sin(a) * z.r);
        c.rotation.y = -a;  // wings along the rim, flying round it
        flap(c, view.t * 14 + i);
      });
    }
    for (const b of view.barriers) {  // Павеза: a tall shield board with the team cross
      if (born('barrier:' + b.id, SKILLS.durer.W.life - b.left)) MOMENTS.births.barrier(b);
      const v = visualFor(b, () => {
        const g = new THREE.Group();
        const board = mk(new THREE.BoxGeometry(b.half * 2, 1.7, 0.16), 0xd8cfb8, { team: 'unit' });
        board.position.y = 0.85;
        const cv = mk(new THREE.BoxGeometry(0.28, 1.4, 0.2), TEAM[b.team], { team: b.team });
        cv.position.y = 0.85;
        const ch = mk(new THREE.BoxGeometry(1.4, 0.26, 0.2), TEAM[b.team], { team: b.team });
        ch.position.y = 1.1;
        g.add(board, cv, ch);
        return g;
      });
      const k = Math.min(1, b.left / 0.25);
      v.position.set(b.center.x, (k - 1) * 1.7, b.center.z);
      v.rotation.y = Math.atan2(b.dir.x, b.dir.z);
    }
    for (const r of view.reveals) {  // Прорицание: a ring of stars over the revealed ground
      if (born('reveal:' + r.id, SKILLS.astrologer.W.life - r.left)) MOMENTS.births.reveal(r);
      const v = visualFor(r, () => {
        const g = new THREE.Group();
        g.add(flat(fxMesh(new THREE.RingGeometry(0.97, 1, 72), 0xb8c8ff), 0));
        for (let i = 0; i < 8; i++) g.add(fxMesh(new THREE.OctahedronGeometry(0.18), 0xd8e0ff));
        return g;
      });
      v.position.set(r.x, 0.3, r.z);
      v.children[0].scale.setScalar(r.r);
      for (let i = 0; i < v.children.length - 1; i++) {
        const st = v.children[1 + i], a = i / 8 * Math.PI * 2 + view.t * 0.4;
        st.position.set(Math.cos(a) * r.r, 0.6 + Math.sin(view.t * 3 + i) * 0.15, Math.sin(a) * r.r);
        st.rotation.y = view.t * 3;
      }
    }
    // `mine`/`near` filtering already happened in GameView.traps itself (LocalGameView's own comment
    // there explains why offline is deliberately more permissive than the wire's own rule).
    for (const tr of view.traps) {
      // armIn counts down only until armed: past that its age is unknown, and long out of the window anyway
      if (tr.armIn > 0 && born('trap:' + tr.id, SKILLS.arquebusier.W.trap.arm - tr.armIn)) MOMENTS.births.trap(tr);
      const v = visualFor(tr, () => {
        const g = new THREE.Group();
        const vial = mk(new THREE.CylinderGeometry(0.14, 0.18, 0.4, 8), 0xc8a050, { cast: false });
        vial.position.y = 0.2; vial.rotation.z = 1.2;
        const glow = flat(fxMesh(new THREE.RingGeometry(0.55, 0.7, 24), 0xd2d86a), 0.05);
        g.add(vial, glow);
        return g;
      });
      v.position.set(tr.pos.x, 0.25, tr.pos.z);
      v.children[1].visible = tr.armIn <= 0;
      v.children[1].scale.setScalar(1 + Math.sin(view.t * 5) * 0.12);
    }
    // Тотем Стойкости / Тотем Гнева: a carved post seen by both teams once in sight (no stealth, unlike a
    // trap) — Стойкости warm wood crowned with a sun disc, Гнева dark red with horns. Its ring marks the
    // reach; each pulse lifts motes inside it. The two hit points are two studs on the post, put out one at
    // a time as strikeTotem() lands (`hp` is always 0/1/2 here).
    for (const t of view.totems) {
      const look = TOTEM_LOOK[t.kind], def = totemDef(t.kind);
      const v = visualFor(t, () => {
        const g = new THREE.Group();
        const ring = flat(fxMesh(new THREE.RingGeometry(0.95, 1, 72), look.ring), 0);
        const inner = flat(fxMesh(new THREE.RingGeometry(0.97, 1, 72), look.ring), 0.01);
        const post = mk(new THREE.CylinderGeometry(0.14, 0.2, 1.3, 6), look.wood, { cast: false });
        post.position.y = 0.65;
        const crown = new THREE.Group();
        crown.position.y = 1.45;
        if (t.kind === 'ward') {
          const sun = mk(new THREE.CylinderGeometry(0.3, 0.3, 0.07, 12), look.mark, { cast: false });
          sun.rotation.x = Math.PI / 2;
          crown.add(sun);
          for (let i = 0; i < 8; i++) {
            const ray = mk(new THREE.ConeGeometry(0.05, 0.18, 3), look.mark, { cast: false }), a = i / 8 * Math.PI * 2;
            ray.position.set(Math.cos(a) * 0.4, Math.sin(a) * 0.4, 0); ray.rotation.z = a - Math.PI / 2;
            crown.add(ray);
          }
        } else {
          const head = mk(new THREE.BoxGeometry(0.42, 0.26, 0.3), look.wood, { cast: false });
          crown.add(head);
          for (const s of [-1, 1]) {
            const horn = mk(new THREE.ConeGeometry(0.07, 0.5, 5), look.mark, { cast: false });
            horn.position.set(s * 0.27, 0.22, 0); horn.rotation.z = -s * 0.55;
            crown.add(horn);
          }
        }
        const hp1 = mk(new THREE.SphereGeometry(0.07, 8, 6), look.mark, { cast: false });
        hp1.position.set(-0.1, 1.05, 0.17);
        const hp2 = mk(new THREE.SphereGeometry(0.07, 8, 6), look.mark, { cast: false });
        hp2.position.set(0.1, 1.05, 0.17);
        g.add(ring, inner, post, crown, hp1, hp2);
        g.userData.pulse = -1;
        return g;
      });
      const age = Math.max(0, def.life - t.left), grow = Math.min(1, age / 0.2);
      v.position.set(t.pos.x, 0.27, t.pos.z);
      v.children[0].scale.setScalar(t.r * grow + 0.001);
      v.children[1].scale.setScalar(t.r * ((age / def.interval) % 1) + 0.001);  // a ripple per pulse
      v.children[3].rotation.y = view.t * 0.6;
      v.children[4].visible = t.hp >= 2;
      v.children[5].visible = t.hp >= 1;
      const pulse = Math.floor(age / def.interval);
      if (pulse !== v.userData.pulse) {
        v.userData.pulse = pulse;
        for (let i = 0; i < 4; i++) {
          const a = Math.random() * Math.PI * 2, d = Math.sqrt(Math.random()) * t.r * 0.9;
          puff(new THREE.Vector3(t.pos.x + Math.cos(a) * d, 0.3, t.pos.z + Math.sin(a) * d), look.mote, { size: 0.5, rise: 1.2, dur: 0.7 });
        }
      }
    }
  }

  /* Death: fall (clip) → colour drains → ink floods the body → it sinks into a spreading ink pool → gone. */
  const DRAIN: [number, number] = [0.4, 1.2], FLOOD: [number, number] = [1.2, 2.4], SINK: [number, number] = [2.4, 3.4];
  const ramp = (d: number, [a, b]: [number, number]) => Math.max(0, Math.min(1, (d - a) / (b - a)));
  function deaths(rootOf: (h: ViewHero) => THREE.Object3D) {
    for (const h of view.heroes) {
      const s = fxOf(h), root = rootOf(h);
      if (h.alive) {
        if (s.dying) { setDying(root, null); root.scale.set(1, 1, 1); s.dying = false; }
        continue;
      }
      const d = view.statusOf(h).deadFor ?? 0;
      s.dying = true;
      const drain = ramp(d, DRAIN), flood = ramp(d, FLOOD), sink = ramp(d, SINK);
      setDying(root, drain < 1 ? 0.03 : 0.03 + flood * 0.97);
      root.position.y = -sink * 0.9;
      root.scale.set(1 + sink * 0.25, Math.max(0.01, 1 - sink * 0.85), 1 + sink * 0.25);
      if (sink >= 1) root.visible = false;
      if (d >= FLOOD[0] && !s.poolAt) {
        s.poolAt = view.t;
        spawn('deathpool', () => flat(fxMesh(BLOTS[0] as THREE.ShapeGeometry, INK), 0.22), 4.2, (it) => {
          it.obj.position.set(h.pos.x, 0.22, h.pos.z); it.obj.rotation.z = Math.random() * 6;
        }, (it, k) => {
          const grow = Math.min(1, k * 2.2), fade = k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
          it.obj.scale.setScalar(Math.max(0.001, 1.9 * grow * fade));
        });
      }
    }
    for (const h of view.heroes) if (h.alive) fxOf(h).poolAt = 0;
  }

  function statuses(rootOf: (h: ViewHero) => THREE.Object3D) {
    const confused = viewerConfused(view);
    for (const h of view.heroes) {
      const s = fxOf(h), root = rootOf(h), st = view.statusOf(h);
      // The look itself: heroLook.ts. A dead rig is left to deaths()' dying mask; re-applied when the rig's
      // content changes (a model mounting over its stand-in).
      if (h.alive) {
        const { look, swap, key } = heroMaskLook(view, h, confused), on = root.children[0];
        if (key !== (s.lookKey ?? 'normal') || (key !== 'normal' && on !== s.lookOn)) { setMaskLook(root, look, swap); s.lookKey = key; s.lookOn = on; }
      }
      if (!h.alive || !unitVisible(h)) { s.invisible = false; s.flock = false; continue; }
      (s.seenAt ??= { x: 0, z: 0 }).x = h.pos.x; s.seenAt.z = h.pos.z; s.seenFrame = frame;
      if (!!st.invisible !== !!s.invisible) { if (s.invisible) dusk(h.pos); s.invisible = !!st.invisible; }  // `stealth` covers the way in
      // Стая: the body is gone, a flock flies the hop arc in its place; they scatter where it lands.
      if (h.state === 'flock') {
        root.visible = false;
        const v = visualFor('flock:' + h.id, () => {
          const g = new THREE.Group();
          for (let i = 0; i < 9; i++) { const c = crowMesh(); c.scale.setScalar(1.4); g.add(c); }
          return g;
        });
        v.position.set(h.pos.x, 0, h.pos.z);
        v.rotation.y = h.yaw;
        v.children.forEach((c: any, i: number) => {
          const a = i * 2.4 + view.t * 2.2, r = 0.3 + 0.45 * ((i * 0.618) % 1);
          c.position.set(Math.cos(a) * r, 1 + h.lift * 1.3 + (i % 3) * 0.22 + Math.sin(view.t * 7 + i) * 0.12, Math.sin(a) * r * 0.7 + ((i % 4) - 1.5) * 0.25);
          c.rotation.y = Math.sin(view.t * 3 + i) * 0.4;
          flap(c, view.t * 16 + i * 1.3);
        });
        s.flock = true;
      } else if (s.flock) { s.flock = false; scatter(h.pos); }
      if (view.t < h.stunUntil) {
        const v = visualFor('stun:' + h.id, () => {
          const g = new THREE.Group();
          for (let i = 0; i < 3; i++) g.add(fxMesh(new THREE.OctahedronGeometry(0.12), 0xe8c050));
          return g;
        });
        v.position.set(h.pos.x, 2.85 + h.lift, h.pos.z);
        v.children.forEach((s: any, i: number) => { const a = view.t * 5 + i * 2.09; s.position.set(Math.cos(a) * 0.45, Math.sin(a * 2) * 0.06, Math.sin(a) * 0.45); });
      }
      if (st.slowed) {
        const v = visualFor('slow:' + h.id, () => flat(fxMesh(new THREE.RingGeometry(1.15, 1.4, 36, 1, 0, 4.2), 0x6a7a9a), 0.29));
        v.position.set(h.pos.x, 0.29, h.pos.z);
        v.rotation.z = -view.t * 1.5;
      }
      if (st.shielded) {
        const v = visualFor('ward:' + h.id, () => {
          const g = new THREE.Group();
          g.add(fxMesh(new THREE.TorusGeometry(0.85, 0.04, 6, 40), 0xd8e08a), fxMesh(new THREE.TorusGeometry(0.7, 0.04, 6, 40), 0xd8e08a));
          return g;
        });
        v.position.set(h.pos.x, 1.1 + h.lift, h.pos.z);
        v.children[0].rotation.set(Math.PI / 2 + 0.4, view.t * 2, 0);
        v.children[1].rotation.set(Math.PI / 2 - 0.5, -view.t * 2.5, 0);
        v.children[1].position.y = 0.6;
      }
      // Клеймо — both teams read this the same way (docs' own "warnings visible to both teams" — the
      // player being hit needs to see why as much as the caster).
      if (st.brandFor > 0) {
        const v = visualFor('brandMark:' + h.id, () => fxMesh(new THREE.BoxGeometry(0.16, 0.16, 0.04), 0xc8501e));
        v.position.set(h.pos.x + 0.22, 2.75 + h.lift, h.pos.z);
        v.rotation.y = view.t * 2;
      }
      if (st.taunted) {
        // The tether line is offline-only (GameView.tetherSource's own header: the wire never sends who is
        // holding the tether, to avoid leaking that hero's position through fog) — the presence marker
        // below still shows either way, matching remoteFx.ts's existing simplification for the online case.
        const by = view.tetherSource(h);
        if (by) {
          const line = visualFor('tether:' + h.id, () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), 0xe0604a), 0.33));
          const dx = by.pos.x - h.pos.x, dz = by.pos.z - h.pos.z;
          line.scale.set(0.12, Math.max(0.01, Math.hypot(dx, dz)), 1);
          line.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) + Math.PI);
          line.position.set((h.pos.x + by.pos.x) / 2, 0.33, (h.pos.z + by.pos.z) / 2);
        }
        const v = visualFor('taunt:' + h.id, () => fxMesh(new THREE.ConeGeometry(0.14, 0.5, 4), 0xe0604a));
        v.position.set(h.pos.x, 3.0 + Math.sin(view.t * 10) * 0.08, h.pos.z);
        v.rotation.set(Math.PI, view.t * 4, 0);
      }
      // Связка: a cord to the other end, when the viewer sees where that is (placeholder look) — once the
      // thrown coil has landed: core ties on release, the throw is only drawn flying
      if (!st.tetherTo) s.tiedAt = undefined;
      else s.tiedAt ??= view.t;
      if (st.tetherTo && view.t - s.tiedAt! >= BIND_FLIGHT) {
        const to = st.tetherTo, line = visualFor('bind:' + h.id, () => flat(fxMesh(new THREE.PlaneGeometry(1, 1), 0xb07a5a), 0.34));
        const dx = to.x - h.pos.x, dz = to.z - h.pos.z;
        line.scale.set(0.09, Math.max(0.01, Math.hypot(dx, dz)), 1);
        line.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) + Math.PI);
        line.position.set((h.pos.x + to.x) / 2, 0.34, (h.pos.z + to.z) / 2);
      }
      if (st.hasted && view.t - (s.hasteAt || 0) > 0.12 && view.movingOf(h)) { s.hasteAt = view.t; puff(h.pos.clone().add(new THREE.Vector3(0, 0.2, 0)), 0xcbbf9f, { size: 0.6, rise: 0.2, dur: 0.4 }); }
      // Липкое пламя's afterburn: a small ember puff off the hero, same cadence as the haste dust above.
      if (st.burning && view.t - (s.burnAt || 0) > 0.2) { s.burnAt = view.t; puff(h.pos.clone().add(new THREE.Vector3(0, 0.9 + h.lift, 0)), 0xff8a3c, { size: 0.35, rise: 0.5, dur: 0.4 }); }
      if (st.enraged) {
        const v = visualFor('rage:' + h.id, () => flat(fxMesh(new THREE.RingGeometry(1.15, 1.45, 36, 1, 0, 5), 0x9a2a20), 0.31));
        v.position.set(h.pos.x, 0.31, h.pos.z);
        v.rotation.z = view.t * 3;
      }
      if (st.hasHot && view.t - (s.hotAt || 0) > 0.3) { s.hotAt = view.t; (handlers.prayer as any)({ hero: h }); }
      if (st.empowered || st.feintArmed) {
        const color = st.empowered ? 0xf2d46b : 0xd6b8f0;
        const v = visualFor('armed:' + h.id + color, () => flat(fxMesh(new THREE.RingGeometry(1.25, 1.4, 36), color), 0.3));
        v.position.set(h.pos.x, 0.3, h.pos.z);
        v.scale.setScalar(1 + Math.sin(view.t * 8) * 0.08);
      }
      /* ---- Астрал marks, each one shape over the head so they never read as the stun's gold stars ---- */
      if (st.forced === 'charm') {  // Соблазн: a heart, and a beaded thread to Грех while he's in sight
        const by = view.heroes.find(o => o.kind === 'sin' && o.team !== h.team && o.alive && unitVisible(o));
        if (by) thread(h, by);
        const v = visualFor('heart:' + h.id, () => fxMesh(heartGeometry(), CRIMSON));
        v.position.set(h.pos.x + 0.65, 2.3 + h.lift + Math.sin(view.t * 6) * 0.05, h.pos.z);
        v.rotation.set(-0.5, Math.sin(view.t * 2) * 0.5, 0);
        v.scale.setScalar(1.5 + Math.max(0, Math.sin(view.t * 9)) * 0.2);
      }
      if (st.forced === 'fear') {  // Стая's fear: three fright strokes fanned over the head, trembling
        const v = visualFor('fright:' + h.id, () => {
          const g = new THREE.Group();
          for (let i = 0; i < 3; i++) {
            const m = fxMesh(new THREE.CylinderGeometry(0.035, 0.015, 0.34, 5), BRUISE);
            m.position.set((i - 1) * 0.2, 0.12 - Math.abs(i - 1) * 0.06, 0);
            m.rotation.z = -(i - 1) * 0.5;
            g.add(m);
          }
          return g;
        });
        v.position.set(h.pos.x + 0.65 + Math.sin(view.t * 40) * 0.03, 2.3 + h.lift, h.pos.z);
        v.scale.setScalar(1.4);
      }
      if (st.confused) {  // Наваждение: a question hook rocking over the head
        const v = visualFor('lost:' + h.id, () => {
          const g = new THREE.Group();
          const hook = fxMesh(new THREE.TorusGeometry(0.13, 0.035, 6, 16, Math.PI * 1.4), 0x8a7ab8);
          hook.position.y = 0.2; hook.rotation.z = -0.9;
          const dot = fxMesh(new THREE.SphereGeometry(0.045, 8, 6), 0x8a7ab8);
          g.add(hook, dot);
          return g;
        });
        v.position.set(h.pos.x - 0.65, 2.2 + h.lift, h.pos.z);
        v.rotation.z = Math.sin(view.t * 4) * 0.35;
        v.scale.setScalar(1.4);
      }
      if (st.sleepless) {  // Бессонница: an open eye beside the head
        const v = visualFor('eye:' + h.id, () => {
          const g = new THREE.Group();
          const lid = fxMesh(new THREE.TorusGeometry(0.14, 0.03, 6, 20), BRUISE);
          lid.scale.y = 0.5;
          const pupil = fxMesh(new THREE.SphereGeometry(0.06, 8, 6), CROW);
          g.add(lid, pupil);
          return g;
        });
        v.position.set(h.pos.x - 0.65, 1.65 + h.lift, h.pos.z);
        v.scale.setScalar(1.4);
      }
    }
    // Клеймо on a monster: the hero's seal, scaled up and hung above the hp plate.
    for (const m of view.monsters) {
      if (m.brandFor <= 0) continue;
      const v = visualFor('brandMark:m' + m.id, () => fxMesh(new THREE.BoxGeometry(0.3, 0.3, 0.06), 0xc8501e));
      v.position.set(m.pos.x, MONSTER_LOOK[m.kind].plateY + 0.45, m.pos.z);
      v.rotation.y = view.t * 2;
    }
  }

  /* Соблазн's thread: beads sagging from the victim's chest to Грех's, drifting a little. */
  function thread(h: ViewHero, by: ViewHero) {
    const N = 14;
    const v = visualFor('thread:' + h.id, () => {
      const g = new THREE.Group();
      for (let i = 0; i < N; i++) g.add(fxMesh(new THREE.SphereGeometry(0.075, 8, 6), CRIMSON));
      return g;
    });
    v.children.forEach((b: any, i: number) => {
      const u = (i + 0.5) / N, sag = Math.sin(Math.PI * u) * 0.45;
      b.position.set(h.pos.x + (by.pos.x - h.pos.x) * u, 1.3 + h.lift + (by.lift + 0.2 - h.lift) * u - sag + Math.sin(view.t * 6 + u * 10) * 0.04,
        h.pos.z + (by.pos.z - h.pos.z) * u);
    });
  }
  /* Стая landing: the crows break outward and up. */
  function scatter(at: { x: number; z: number }) {
    for (let i = 0; i < 7; i++) {
      const a = i / 7 * Math.PI * 2 + Math.random() * 0.5;
      spawn('crow', crowMesh, 0.8, (it) => {
        (it.p ||= new THREE.Vector3()).set(at.x, 1, at.z); it.a = a; it.ph = Math.random() * 6;
        it.obj.rotation.y = Math.PI / 2 - a;
      }, (it, k) => {
        it.obj.position.set(it.p.x + Math.cos(it.a) * k * 4.5, it.p.y + k * 2.8, it.p.z + Math.sin(it.a) * k * 4.5);
        flap(it.obj, it.ph + k * 40);
        it.obj.scale.setScalar(Math.max(0.01, 1 - k * k));
      });
    }
  }

  /* Resolve a `hero` id on an fx event to the live ViewHero before handing it to a handler — everything
     above is otherwise identical to the prototype's fx.js, which held real hero refs. */
  function resolveHero(e: any) { return 'hero' in e ? { ...e, hero: view.heroes[e.hero] } : e; }

  /* Расплата: no event says a blow was shared, so it is read off the picture. A shared blow is two hits in
     the same batch — Грех's share on him and the rest on his ally — so a stroke is drawn only when Грех
     (at his ring's centre, the ring follows him) was hit too, and only for a hit whose nearest seen hero is
     an ally of his inside the ring: a stray bolt fizzling out or a blow on an enemy beside the ally draws none. */
  const NEAR = 1;  // online the hit spot is a snapshot position, the heroes interpolated behind it
  function sharedBlows(hits: readonly { x: number; z: number }[]) {
    if (hits.length < 2) return;
    const d = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
    for (const z of view.pools) {
      if (z.look !== 'atonement') continue;
      const sin = view.heroes.find(o => o.kind === 'sin' && o.alive && unitVisible(o) && d(o.pos, z.pos) < 1.5);
      if (!sin || !hits.some(p => d(p, sin.pos) < NEAR)) continue;
      for (const p of hits) {
        if (d(p, sin.pos) < NEAR) continue;
        let near: ViewHero | null = null;
        for (const h of view.heroes) if (h.alive && unitVisible(h) && d(h.pos, p) < NEAR && (!near || d(h.pos, p) < d(near.pos, p))) near = h;
        if (near && near !== sin && near.team === sin.team && d(near.pos, z.pos) <= z.r) link(near.pos, sin.pos);
      }
    }
  }

  function update(rootOf: (h: ViewHero) => THREE.Object3D) {
    frame++;
    rootOfNow = rootOf;
    // Offline the events are core's own, unfiltered: a hero's event is as visible as that hero (grass,
    // Слиться), like server/snapshot.ts's eventVisible. Online and in a replay the server already filtered
    // them, and a hero stepping out of grass is still `visible: false` here for the interpolation delay —
    // re-checking him would drop his first swing, so there only the spot counts, as before.
    const local = view instanceof LocalGameView;
    const hits: { x: number; z: number }[] = [];
    for (const e of view.events) {
      if (e.type !== 'fx') continue;
      const re = resolveHero(e);
      // An id no hero has, or a spot that isn't one (a hand-edited replay, a newer server): nothing to draw it on.
      if (('hero' in e && !re.hero) || ('pos' in e && !isVec(re.pos))) continue;
      const at = 'pos' in re ? re.pos : (re.hero && re.hero.pos);
      // Unseen: no picture, no sound.
      if (local && re.hero ? !unitVisible(re.hero) : (at && !posVisible(at) && !(re.hero && re.hero.team === view.player!.team))) continue;
      if (handlers[re.name]) (handlers[re.name] as any)(re);
      if (sound) sound.play(re.name, at, { big: (re as any).big, hero: re.hero });
      if (re.name === 'hit') hits.push(re.pos);
    }
    sharedBlows(hits);
    for (let i = live.length - 1; i >= 0; i--) {
      const it = live[i], k = (view.t - it.t0) / it.dur;
      // A stale `t0` (ticked against a different clock than it was captured against) or a zero/garbage
      // `dur` both surface here as `k` — this is the general "r = f(now - t0)" family the giant-red-disc
      // report named, so treated the same as a normally-expired item (hidden, freed, never handed to a
      // tick() callback) rather than passed through with an astronomical value.
      if (k >= 1 || !saneProgress(k, it.key)) { it.obj.visible = false; live.splice(i, 1); (free[it.key] || (free[it.key] = [])).push(it); continue; }
      it.tick(it, Math.max(0, k));
    }
    // `view.projectiles`/`.lobs` are already visibility-filtered at the source (both GameView
    // implementations), unlike before the unification when this loop did that filtering itself.
    for (const p of view.projectiles) projectileVisual(p);
    headings.sweep();
    for (const l of view.lobs) lobVisual(l);
    telegraphs();
    zones();
    for (const key of births) if (!birthsListed.has(key)) births.delete(key);
    birthsListed.clear();
    deaths(rootOf);
    statuses(rootOf);
    keyed.forEach(sweep);
  }
  function sweep(v: THREE.Object3D, owner: any) {
    if (v.userData.touched === frame) return;
    v.visible = false;
    if (!oneOff.has(owner)) return;
    scene.remove(v); release(v); keyed.delete(owner); oneOff.delete(owner);  // finished projectiles, pools, traps
  }

  /** Removes every mesh this instance ever added to `scene` — `scene` outlives one `createFx()` call
      whenever the same tab plays more than one match (main.ts today reloads the offline picker fresh each
      time so this has never mattered there, but the online lobby's "play again" loop does not reload at
      all, and this used to be exactly the class of bug net/remoteFx.ts's own teardown was added to fix —
      an fx instance abandoned mid-effect otherwise orphans its meshes in `scene` forever, rendering under
      the next match). Called by net/onlineMatch.ts's own finish(); main.ts has no equivalent call site yet
      since offline has never needed one, not because this is online-only. */
  function destroy() {
    for (const it of live) { scene.remove(it.obj); release(it.obj); }
    live.length = 0;
    for (const key of Object.keys(free)) { for (const it of free[key]!) { scene.remove(it.obj); release(it.obj); } delete free[key]; }
    for (const v of keyed.values()) { scene.remove(v); release(v); }
    keyed.clear();
    oneOff.clear();
    births.clear();
    birthsListed.clear();
  }

  return { update, destroy };
}
